import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { CACHE_CONTROL_NO_CACHE, CACHE_CONTROL_SHORT_TTL } from '@src/constants';
import { PageByAppAndAlternatePathDocument, PageFragment } from '@src/lib/__generated/sdk';
import { getClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import { ChatBot, NextPage, NextPageHead, PageView, VWOExperimentPush } from '@src/lib/shared-components-web';
import { PageThemeContextProvider } from '@src/lib/shared-components-web/context/PageThemeProvider.ctx';
import { LAYOUT_FEATURE_FLAGS } from '@src/lib/vwo-fme/layout-feature-flags';
import { resolveFeatureFlags } from '@src/lib/vwo-fme/resolve-feature-flags';
import { resolveServerSideExperiments } from '@src/lib/vwo-fme/resolve-server-side-experiments';
import { fetchAppData } from '@utils/pages/app-data-fetcher';
import { generateBreadcrumbs } from '@utils/pages/breadcrumb-generator';
import choosePreview from '@utils/pages/choose-preview';
import { describeContentfulError, throwIfContentfulUnavailable } from '@utils/pages/contentful-errors';
import DetermineIndexing from '@utils/pages/determine-indexing';
import getConfigByBrand from '@utils/pages/get-config-by-brand';
import getConfigServices from '@utils/pages/get-config-services';
import { getDeviceTypeFromHeaders } from '@utils/pages/get-device-type-from-headers';
import getHostFromHeaders from '@utils/pages/get-host-from-headers';
import { getServerSideTranslations } from '@utils/pages/get-serverside-translations';
import { checkIsFacilityDetailsRoute, getFacilityDetailsRoutePaths } from '@utils/pages/get-static-props-config';
import { handlePageLoadFailure } from '@utils/pages/handle-page-load-failure';
import { processPageContents } from '@utils/pages/page-contents-processor';
import { trimContentfulProps } from '@utils/pages/trim-contentful-props';
const Page = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const pageData = useContentfulLiveUpdates(props.page);
  const {
    url: logoUrl
  } = props.appData?.logo ?? {};
  const {
    path,
    title,
    indexed,
    description,
    canonical,
    disableChatBot = false,
    analyticsContext,
    ogImage
  } = pageData;
  const pageTheme = props.page?.pageTheme || 'Primary';
  const {
    siteURL,
    features,
    featureFlags,
    logo
  } = props.config as BrandThemeConfig;
  const {
    preview,
    breadcrumbsData,
    pageReferrer,
    domain
  } = props;
  return <PageThemeContextProvider pageTheme={pageTheme} data-sentry-element="PageThemeContextProvider" data-sentry-component="Page" data-sentry-source-file="[...slug].page.tsx">
			<PageView analyticsContext={analyticsContext} pageReferrer={pageReferrer} siteSection="Marketing" data-sentry-element="PageView" data-sentry-source-file="[...slug].page.tsx" />
			<NextPageHead path={path} title={title} indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite, domain)} description={description} canonical={canonical} breadcrumbTitle={breadcrumbsData?.breadcrumbTitle} breadcrumbs={breadcrumbsData?.breadcrumbsCollection.items} originUrl={siteURL} ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark} contentsCollection={pageData.contentsCollection} data-sentry-element="NextPageHead" data-sentry-source-file="[...slug].page.tsx" />
			<VWOExperimentPush decisions={props.vwoDecisions ?? []} data-sentry-element="VWOExperimentPush" data-sentry-source-file="[...slug].page.tsx" />
			<NextPage pageData={{
      ...pageData
    }} data-sentry-element="NextPage" data-sentry-source-file="[...slug].page.tsx" />
			{!disableChatBot && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
		</PageThemeContextProvider>;
};
export const getServerSideProps: GetServerSideProps = async ({
  resolvedUrl,
  params,
  query,
  locale,
  req,
  res,
  preview = false
}) => {
  if (!params?.slug) {
    return {
      notFound: true
    };
  }
  const domain = getHostFromHeaders(req?.headers);
  const tagTheme: TagThemeType = 'lovet';
  const logger = getLogger('landing-page', tagTheme);
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  const pageReferrer = req.headers.referer || '';
  const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';
  try {
    const slugs = params.slug as string[];
    const path = '/' + slugs.join('/');

    // Skip internal Next.js paths (e.g., /_next/*, webpack hot-updates)
    if (path.startsWith('/_next/') || path.startsWith('/.well-known/')) {
      return {
        notFound: true
      };
    }
    const brandConfig = getConfigByBrand(tagTheme);
    preview = choosePreview(domain, preview);

    // Locations/facility-detail pages live in a separate Contentful app and are served by their own
    // dedicated routes (src/pages/locations/**); never query the marketing app's Page content for them.
    const routeBasePaths = getFacilityDetailsRoutePaths(brandConfig.features?.otherApps?.facilityDetailsBasePath);
    if (checkIsFacilityDetailsRoute(path.replace(/^\//, ''), routeBasePaths)) {
      return {
        notFound: true
      };
    }
    const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';
    const gqlClient = getClient(preview);
    if (gqlClient === null) {
      logger.warn('GQL_CLIENT_IS_NULL: missing token for fetching contentful data');
      throw new Error('gqlClient is null');
    }
    const appData = await fetchAppData(gqlClient, appName, preview, logger);
    if (!appData) {
      return {
        notFound: true
      };
    }
    const alternativePath = path.endsWith('/') ? path : path + '/';
    const pageData = await gqlClient.query(PageByAppAndAlternatePathDocument, {
      appName,
      path,
      alternativePath,
      preview
    }).toPromise();
    throwIfContentfulUnavailable(pageData, `CONTENTFUL_ERROR_SLUG_PAGE: PATH - ${path}`);
    if (pageData.error) {
      logger.warn(`CONTENTFUL_ERROR_SLUG_PAGE: PATH - ${path} - ERROR: ${describeContentfulError(pageData.error)}`);
    }
    const page = pageData.data?.pageCollection?.items[0] as PageFragment;
    if (!page) {
      logger.warn(`COULD_NOT_FIND_THE_PAGE_WITH_PATH: ${path}`);
      return {
        notFound: true
      };
    }

    // Process page contents: fetch by ID, enrich with entry links
    const enrichedPageContents = await processPageContents(gqlClient, preview, logger, page?.contentsCollection?.items, path);

    // Resolve VWO FME feature flags for Server-Side ExperimentContainers
    const {
      decisions: experimentDecisions
    } = await resolveServerSideExperiments(enrichedPageContents, brandConfig as any, req, res, query, logger);

    // Resolve standalone VWO FME flags (layout-level header swap, etc.)
    const {
      flags: serverFeatureFlags,
      decisions: flagDecisions
    } = await resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, brandConfig as any, req, res, query, logger);
    const vwoDecisions = [...experimentDecisions, ...flagDecisions];

    // breadcrumb data generation from url path if not in page data
    const breadcrumbsData = await generateBreadcrumbs(gqlClient, appName, slugs, page, preview, logger);
    if (preview) {
      res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
    } else if (!res.getHeader('Cache-Control')) {
      // Skip if an earlier resolver already pinned the response to a
      // non-shareable Cache-Control (e.g. visitor-id mint sets
      // "private, no-store" to prevent CDN cookie poisoning).
      res.setHeader('Cache-Control', CACHE_CONTROL_SHORT_TTL);
    }
    let hasIpBasedCoordinates = false;
    let coordinates = {
      latitude: brandConfig.defaultCoordinates?.latitude || 0,
      longitude: brandConfig.defaultCoordinates?.longitude || 0
    };
    if (req.headers['cloudfront-viewer-latitude'] && req.headers['cloudfront-viewer-longitude']) {
      hasIpBasedCoordinates = true;
      coordinates = {
        latitude: Number.parseFloat(req.headers['cloudfront-viewer-latitude'] as string),
        longitude: Number.parseFloat(req.headers['cloudfront-viewer-longitude'] as string)
      };
      logger.debug(`CDN_GEO_LOCATION_FOUND: [${coordinates?.latitude}, ${coordinates?.longitude}]`);
    } else {
      logger.debug(`CDN_GEO_LOCATION_MISS`);
    }
    const transformedServices = getConfigServices(brandConfig as any);
    // Live Preview (useContentfulLiveUpdates) needs the untrimmed Contentful shape
    const trimForClient = <T,>(data: T): T => preview ? data : trimContentfulProps(data);
    return {
      props: {
        ...(await getServerSideTranslations(locale)),
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME
        },
        config: {
          ...brandConfig,
          ...(appData.featureFlags ? {
            featureFlags: appData.featureFlags
          } : {}),
          services: transformedServices,
          schemaUrl: `${brandConfig.siteURL}${req.url}`
        },
        page: trimForClient({
          ...page,
          contentsCollection: {
            items: enrichedPageContents
          }
        }),
        user: {
          coordinates,
          hasIpBasedCoordinates
        },
        appData: trimForClient(appData),
        preview,
        breadcrumbsData,
        deviceType,
        pageReferrer,
        stateCodeFromHeaders,
        domain: domain ?? '',
        vwoDecisions,
        serverFeatureFlags,
        hideNavigation: (enrichedPageContents?.some(item => item?.__typename === 'Quiz' && (item as {
          quizType?: string;
        }).quizType === 'Pathfinder') ?? false) || ['/lp/meta-result-candidate', '/lp/meta-result-call'].includes(path.replace(/\/$/, ''))
      }
    };
  } catch (e) {
    return handlePageLoadFailure(logger, resolvedUrl, e);
  }
};
export default Page;